/**
 * BooksMediaListings - Browse and filter Books & Media listings
 * Includes sidebar navigation, filters, and listing cards
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Skeleton } from '../components/ui/skeleton';
import { 
  BookOpen, Newspaper, GraduationCap, BookImage, Disc, 
  Download, Gem, Pencil, Search, Filter, Grid, List,
  MapPin, Clock, Eye, Heart, Plus, ChevronRight, ChevronDown,
  SlidersHorizontal, X, ShieldAlert, Star, Phone, ExternalLink, Building
} from 'lucide-react';
import { useCountry } from '../context/CountryContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category configuration
const CATEGORIES = [
  { slug: 'books', name: 'Books', icon: BookOpen, color: 'bg-blue-500' },
  { slug: 'magazines', name: 'Magazines', icon: Newspaper, color: 'bg-pink-500' },
  { slug: 'educational-media', name: 'Educational Media', icon: GraduationCap, color: 'bg-purple-500' },
  { slug: 'comics-graphic-novels', name: 'Comics & Graphic Novels', icon: BookImage, color: 'bg-amber-500' },
  { slug: 'cds-dvds', name: 'CDs & DVDs', icon: Disc, color: 'bg-cyan-500' },
  { slug: 'digital-media', name: 'Digital Media', icon: Download, color: 'bg-red-500' },
  { slug: 'collectibles', name: 'Collectibles', icon: Gem, color: 'bg-emerald-500' },
  { slug: 'stationery', name: 'Stationery', icon: Pencil, color: 'bg-gray-500' }
];

const CONDITIONS = [
  { value: 'new', label: 'New' },
  { value: 'like_new', label: 'Like New' },
  { value: 'good', label: 'Good' },
  { value: 'used', label: 'Used' },
  { value: 'worn', label: 'Worn' }
];

// Listing Card Component
const ListingCard = ({ listing, currencySymbol, onClick }) => {
  const category = CATEGORIES.find(c => c.slug === listing.category);
  const Icon = category?.icon || BookOpen;
  
  return (
    <Card 
      className="hover:shadow-lg transition-all cursor-pointer group"
      onClick={onClick}
      data-testid={`listing-card-${listing.id}`}
    >
      <div className="relative">
        {listing.images && listing.images.length > 0 ? (
          <img 
            src={listing.images[0]} 
            alt={listing.title}
            className="w-full h-48 object-cover rounded-t-lg"
          />
        ) : (
          <div className={`w-full h-48 flex items-center justify-center ${category?.color || 'bg-gray-500'} rounded-t-lg`}>
            <Icon className="w-16 h-16 text-white opacity-50" />
          </div>
        )}
        
        {listing.featured && (
          <Badge className="absolute top-2 left-2 bg-gradient-to-r from-amber-500 to-orange-500">
            Featured
          </Badge>
        )}
        
        {listing.status === 'pending' && (
          <Badge className="absolute top-2 right-2 bg-yellow-500">
            Pending Review
          </Badge>
        )}
        
        <button className="absolute top-2 right-2 p-2 bg-white/80 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
          <Heart className="w-4 h-4 text-gray-600 hover:text-red-500" />
        </button>
      </div>
      
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="font-semibold line-clamp-2 flex-1">{listing.title}</h3>
          <Badge variant="outline" className="shrink-0">
            {CONDITIONS.find(c => c.value === listing.condition)?.label || listing.condition}
          </Badge>
        </div>
        
        <p className="text-2xl font-bold text-blue-600 mb-2">
          {currencySymbol}{listing.price?.toLocaleString() || '0'}
          {listing.negotiable && <span className="text-xs font-normal text-gray-500 ml-1">Negotiable</span>}
        </p>
        
        <div className="flex items-center gap-3 text-sm text-gray-500">
          <span className="flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {listing.location}
          </span>
          <span className="flex items-center gap-1">
            <Eye className="w-3 h-3" />
            {listing.views || 0}
          </span>
        </div>
        
        <div className="flex items-center gap-2 mt-3">
          <div className={`p-1.5 rounded ${category?.color || 'bg-gray-500'}`}>
            <Icon className="w-3 h-3 text-white" />
          </div>
          <span className="text-xs text-gray-600">{category?.name || listing.category}</span>
        </div>
      </CardContent>
    </Card>
  );
};

// Sidebar Category Item
const CategoryItem = ({ category, count, isSelected, onClick, disabled }) => {
  const Icon = category.icon;
  return (
    <button
      onClick={() => !disabled && onClick(category.slug)}
      disabled={disabled}
      className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all ${
        disabled ? 'opacity-50 cursor-not-allowed' :
        isSelected ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'hover:bg-gray-50'
      }`}
      data-testid={`sidebar-category-${category.slug}`}
    >
      <div className={`p-2 rounded-lg ${category.color}`}>
        <Icon className="w-4 h-4 text-white" />
      </div>
      <div className="flex-1 text-left">
        <p className="font-medium text-sm">{category.name}</p>
      </div>
      <Badge variant="secondary" className="text-xs">
        {count || 0}
      </Badge>
      {disabled && <ShieldAlert className="w-4 h-4 text-gray-400" />}
    </button>
  );
};

export default function BooksMediaListings() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { country } = useCountry();
  
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [countryConfig, setCountryConfig] = useState(null);
  const [categoryCounts, setCategoryCounts] = useState({});
  const [providers, setProviders] = useState([]);
  
  // Filters
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [condition, setCondition] = useState(searchParams.get('condition') || '');
  const [priceRange, setPriceRange] = useState([0, 100000]);
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);
  
  const currencySymbol = country === 'UK' ? '£' : 'Rs.';
  const limit = 20;
  
  // Fetch country config
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch(`${API_URL}/api/books-media/config/${country}`);
        const data = await response.json();
        setCountryConfig(data.config);
      } catch (err) {
        console.error('Failed to fetch config:', err);
      }
    };
    fetchConfig();
  }, [country]);
  
  // Fetch providers when category changes
  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const params = new URLSearchParams({ country, limit: '4' });
        if (selectedCategory) params.append('category', selectedCategory);
        
        const response = await fetch(`${API_URL}/api/books-media/providers?${params}`);
        const data = await response.json();
        setProviders(data.providers || []);
      } catch (err) {
        console.error('Failed to fetch providers:', err);
      }
    };
    fetchProviders();
  }, [country, selectedCategory]);
  
  // Fetch listings
  useEffect(() => {
    const fetchListings = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          country: country,
          page: page.toString(),
          limit: limit.toString(),
          status: 'active'
        });
        
        if (selectedCategory) params.append('category', selectedCategory);
        if (searchQuery) params.append('search', searchQuery);
        if (condition) params.append('condition', condition);
        if (priceRange[0] > 0) params.append('min_price', priceRange[0].toString());
        if (priceRange[1] < 100000) params.append('max_price', priceRange[1].toString());
        
        const response = await fetch(`${API_URL}/api/books-media/listings?${params}`);
        const data = await response.json();
        
        setListings(data.listings || []);
        setTotalCount(data.total || 0);
        
        // Calculate category counts (simplified - ideally this would be a separate endpoint)
        const counts = {};
        CATEGORIES.forEach(cat => {
          counts[cat.slug] = data.listings?.filter(l => l.category === cat.slug).length || 0;
        });
        setCategoryCounts(counts);
        
      } catch (err) {
        console.error('Failed to fetch listings:', err);
        setListings([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchListings();
  }, [country, selectedCategory, searchQuery, condition, priceRange, page]);
  
  // Get disabled categories
  const disabledCategories = countryConfig 
    ? Object.entries(countryConfig.subcategories || {})
        .filter(([_, cfg]) => !cfg.enabled)
        .map(([slug]) => slug)
    : [];
  
  // Check if digital media is enabled
  const digitalMediaEnabled = countryConfig?.digital_media_enabled || false;
  if (!digitalMediaEnabled && !disabledCategories.includes('digital-media')) {
    disabledCategories.push('digital-media');
  }
  
  // Handle filter changes
  const handleCategorySelect = (slug) => {
    setSelectedCategory(slug === selectedCategory ? '' : slug);
    setPage(1);
    setSearchParams(prev => {
      if (slug === selectedCategory) {
        prev.delete('category');
      } else {
        prev.set('category', slug);
      }
      return prev;
    });
  };
  
  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearchParams(prev => {
      if (searchQuery) {
        prev.set('search', searchQuery);
      } else {
        prev.delete('search');
      }
      return prev;
    });
  };
  
  const clearFilters = () => {
    setSelectedCategory('');
    setSearchQuery('');
    setCondition('');
    setPriceRange([0, 100000]);
    setPage(1);
    setSearchParams(new URLSearchParams());
  };
  
  const hasActiveFilters = selectedCategory || searchQuery || condition || priceRange[0] > 0 || priceRange[1] < 100000;
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Banner */}
      <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-white/20 rounded-xl">
              <BookOpen className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold">Books & Media</h1>
              <p className="text-white/80">Browse books, magazines, CDs, DVDs and more</p>
            </div>
          </div>
          
          {/* Search Bar */}
          <form onSubmit={handleSearch} className="flex gap-2 max-w-2xl mt-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                placeholder="Search books, authors, titles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white text-gray-900 h-12"
              />
            </div>
            <Button type="submit" size="lg" className="bg-white text-blue-600 hover:bg-gray-100">
              Search
            </Button>
          </form>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex gap-8">
          {/* Sidebar */}
          <aside className="hidden lg:block w-72 shrink-0">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Filter className="w-5 h-5" />
                  Categories
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1">
                {CATEGORIES.map(cat => (
                  <CategoryItem
                    key={cat.slug}
                    category={cat}
                    count={categoryCounts[cat.slug]}
                    isSelected={selectedCategory === cat.slug}
                    onClick={handleCategorySelect}
                    disabled={disabledCategories.includes(cat.slug)}
                  />
                ))}
              </CardContent>
            </Card>
            
            {/* Filters Card */}
            <Card className="mt-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <SlidersHorizontal className="w-5 h-5" />
                  Filters
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Condition</label>
                  <Select value={condition || "all"} onValueChange={(v) => setCondition(v === "all" ? "" : v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Any condition" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Any condition</SelectItem>
                      {CONDITIONS.map(c => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Price Range ({currencySymbol})
                  </label>
                  <Slider
                    value={priceRange}
                    onValueChange={setPriceRange}
                    max={100000}
                    step={100}
                    className="mt-2"
                  />
                  <div className="flex justify-between text-sm text-gray-500 mt-1">
                    <span>{currencySymbol}{priceRange[0].toLocaleString()}</span>
                    <span>{currencySymbol}{priceRange[1].toLocaleString()}</span>
                  </div>
                </div>
                
                {hasActiveFilters && (
                  <Button variant="outline" onClick={clearFilters} className="w-full">
                    <X className="w-4 h-4 mr-2" />
                    Clear Filters
                  </Button>
                )}
              </CardContent>
            </Card>
            
            {/* Post Ad CTA */}
            <Card className="mt-4 bg-gradient-to-br from-blue-500 to-purple-600 text-white">
              <CardContent className="p-6 text-center">
                <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-80" />
                <h3 className="font-bold text-lg mb-2">Sell Your Items</h3>
                <p className="text-sm text-white/80 mb-4">
                  List your books, CDs, DVDs and more for free
                </p>
                <Button 
                  onClick={() => navigate('/books-media/post')}
                  className="w-full bg-white text-blue-600 hover:bg-gray-100"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Post an Ad
                </Button>
              </CardContent>
            </Card>
          </aside>
          
          {/* Main Content */}
          <main className="flex-1">
            {/* Toolbar */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <p className="text-gray-600">
                  <span className="font-semibold">{totalCount}</span> listings found
                </p>
                
                {/* Active filters badges */}
                {selectedCategory && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    {CATEGORIES.find(c => c.slug === selectedCategory)?.name}
                    <X 
                      className="w-3 h-3 cursor-pointer" 
                      onClick={() => handleCategorySelect(selectedCategory)} 
                    />
                  </Badge>
                )}
              </div>
              
              <div className="flex items-center gap-2">
                {/* Mobile filter button */}
                <Button 
                  variant="outline" 
                  className="lg:hidden"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <Filter className="w-4 h-4 mr-2" />
                  Filters
                </Button>
                
                {/* View mode toggle */}
                <div className="flex border rounded-lg">
                  <Button
                    variant={viewMode === 'grid' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('grid')}
                    className="rounded-r-none"
                  >
                    <Grid className="w-4 h-4" />
                  </Button>
                  <Button
                    variant={viewMode === 'list' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('list')}
                    className="rounded-l-none"
                  >
                    <List className="w-4 h-4" />
                  </Button>
                </div>
                
                <Button onClick={() => navigate('/books-media/post')} className="hidden sm:flex">
                  <Plus className="w-4 h-4 mr-2" />
                  Sell Item
                </Button>
              </div>
            </div>
            
            {/* Listings Grid */}
            {loading ? (
              <div className={`grid gap-6 ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3' : 'grid-cols-1'}`}>
                {[...Array(6)].map((_, i) => (
                  <Card key={i}>
                    <Skeleton className="h-48 rounded-t-lg" />
                    <CardContent className="p-4 space-y-2">
                      <Skeleton className="h-5 w-3/4" />
                      <Skeleton className="h-6 w-1/3" />
                      <Skeleton className="h-4 w-1/2" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : listings.length === 0 ? (
              <Card className="text-center py-16">
                <CardContent>
                  <BookOpen className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-xl font-semibold mb-2">No listings found</h3>
                  <p className="text-gray-500 mb-6">
                    {hasActiveFilters 
                      ? 'Try adjusting your filters or search terms'
                      : 'Be the first to post in this category!'}
                  </p>
                  <div className="flex justify-center gap-4">
                    {hasActiveFilters && (
                      <Button variant="outline" onClick={clearFilters}>
                        Clear Filters
                      </Button>
                    )}
                    <Button onClick={() => navigate('/books-media/post')}>
                      <Plus className="w-4 h-4 mr-2" />
                      Post an Ad
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className={`grid gap-6 ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3' : 'grid-cols-1'}`}>
                {listings.map(listing => (
                  <ListingCard
                    key={listing.id}
                    listing={listing}
                    currencySymbol={currencySymbol}
                    onClick={() => navigate(`/listing/${listing.id}`)}
                  />
                ))}
              </div>
            )}
            
            {/* Pagination */}
            {totalCount > limit && (
              <div className="flex justify-center gap-2 mt-8">
                <Button
                  variant="outline"
                  disabled={page === 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  Previous
                </Button>
                <span className="flex items-center px-4 text-gray-600">
                  Page {page} of {Math.ceil(totalCount / limit)}
                </span>
                <Button
                  variant="outline"
                  disabled={page >= Math.ceil(totalCount / limit)}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next
                </Button>
              </div>
            )}
            
            {/* Service Providers Section */}
            {providers.length > 0 && (
              <div className="mt-12 pt-8 border-t">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600">
                      <Building className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold">
                        {selectedCategory 
                          ? `${CATEGORIES.find(c => c.slug === selectedCategory)?.name || 'Related'} Service Providers`
                          : 'Service Providers'
                        }
                      </h2>
                      <p className="text-sm text-gray-500">Trusted providers in your area</p>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {providers.map(provider => {
                    const category = CATEGORIES.find(c => c.slug === provider.category);
                    const Icon = category?.icon || BookOpen;
                    
                    return (
                      <Card key={provider.id} className="group hover:shadow-lg transition-all">
                        <div className="relative h-32 overflow-hidden rounded-t-lg">
                          {provider.images?.[0] ? (
                            <img 
                              src={provider.images[0]} 
                              alt={provider.provider_name}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          ) : (
                            <div className={`w-full h-full bg-gradient-to-br ${category?.color || 'bg-gray-500'} flex items-center justify-center`}>
                              <Icon className="w-12 h-12 text-white opacity-70" />
                            </div>
                          )}
                          
                          {provider.verified && (
                            <Badge className="absolute top-2 left-2 bg-green-500">Verified</Badge>
                          )}
                          
                          {provider.rating && (
                            <div className="absolute top-2 right-2 bg-white/90 px-2 py-1 rounded-full flex items-center gap-1">
                              <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
                              <span className="text-xs font-medium">{provider.rating.toFixed(1)}</span>
                            </div>
                          )}
                        </div>
                        
                        <CardContent className="p-4">
                          <h4 className="font-semibold line-clamp-1 group-hover:text-blue-600 transition-colors">
                            {provider.provider_name}
                          </h4>
                          
                          <p className="text-sm text-gray-500 line-clamp-2 mt-1 min-h-[2.5rem]">
                            {provider.description}
                          </p>
                          
                          <div className="flex items-center gap-2 mt-3 text-xs text-gray-500">
                            <MapPin className="w-3 h-3" />
                            <span>{provider.location}</span>
                          </div>
                          
                          {provider.services_offered && provider.services_offered.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-3">
                              {provider.services_offered.slice(0, 3).map((service, i) => (
                                <Badge key={i} variant="secondary" className="text-xs">
                                  {service}
                                </Badge>
                              ))}
                            </div>
                          )}
                          
                          {provider.phone && (
                            <Button size="sm" variant="outline" className="w-full mt-3" asChild>
                              <a href={`tel:${provider.phone}`}>
                                <Phone className="w-3 h-3 mr-1" />
                                Call Now
                              </a>
                            </Button>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
